import { themeConfig } from '@gap/shared/theme'
import type { GapSnackbarVariant } from './GapSnackbar'

export interface SnackbarVariantStyle {
   surface: string
   accent: string
   accentHover: string
   onAccent: string
   text: string
   border?: string
}

export interface SnackbarSurfaceParams {
   variant?: GapSnackbarVariant
   dark: boolean
   paperColor: string
   shadow: string
}

/**
 * Picks the colours for a snackbar variant from the `themeConfig` ramps.
 * @param params The variant, whether dark mode is on, and the paper colour to fall back to.
 * @returns The surface, accent and text colours for that variant.
 */
export const snackbarVariantStyle = (params: Omit<SnackbarSurfaceParams, 'shadow'>): SnackbarVariantStyle => {
   const { variant, dark, paperColor } = params
   if (!variant) {
      return {
         surface: paperColor,
         accent: themeConfig.primary[800],
         accentHover: themeConfig.primary[900],
         onAccent: '#fff',
         text: themeConfig.primary[900],
         border: themeConfig.primary[200],
      }
   }
   if (variant === 'info') {
      return {
         surface: themeConfig.primary[50],
         accent: themeConfig.primary[800],
         accentHover: themeConfig.primary[900],
         onAccent: '#fff',
         text: themeConfig.primary[900],
      }
   }
   const ramp = themeConfig.system[variant]
   return {
      surface: dark ? ramp[900] : ramp[100],
      accent: dark ? ramp[300] : ramp[600],
      accentHover: dark ? ramp[400] : ramp[700],
      onAccent: dark ? ramp[900] : '#fff',
      text: dark ? ramp[100] : ramp[900],
   }
}

/**
 * Builds the pill surface for MUI's `SnackbarContent`, floating or attached to a document.
 * @param params The variant, dark mode, paper colour and shadow to render with.
 * @returns An `sx` object, inferred rather than `SxProps` so callers can nest it in an array.
 */
export const snackbarSurfaceSx = (params: SnackbarSurfaceParams) => {
   const { surface, text, border } = snackbarVariantStyle(params)
   return {
      bgcolor: surface,
      color: text,
      borderRadius: 9999,
      boxShadow: params.shadow,
      border: border ? `1px solid ${border}` : 'none',
      px: 2,
      py: 0.75,
      minWidth: 'auto',
      '& .MuiSnackbarContent-message': { display: 'flex', alignItems: 'center', gap: 1.25, py: 0.5 },
      '& .MuiSnackbarContent-action': { pl: 1.5, mr: 0, gap: 0.5, alignItems: 'center' },
   }
}
